import { Box, Card, Typography, Grid, Button, Chip, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { getPendingApprovalsSummary, formatCurrency } from "../../../../../utils/mockWalletData";
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WarningIcon from '@mui/icons-material/Warning';

/**
 * PendingApprovalsCard - Shows 5 distinct approval types with reconciliation tracking
 *
 * Displays:
 * 1. Customer Top-ups (Money IN - awaiting verification)
 * 2. Partner Payments (Money OUT - awaiting approval)
 * 3. Approved Payments (Money OUT - awaiting bank transfer)
 * 4. In Transit Payments (Money OUT - awaiting partner confirmation) ⚠️ NEW
 * 5. Channel Commissions (Money OUT - awaiting approval)
 */
const PendingApprovalsCard = () => {
  const navigate = useNavigate();
  const summary = getPendingApprovalsSummary();

  const handleNavigate = (url) => {
    navigate(url);
  };

  // Determine card color based on priority
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'URGENT':
        return 'error.light';
      case 'HIGH':
        return 'warning.light';
      case 'MEDIUM':
        return 'info.light';
      case 'LOW':
        return 'grey.100';
      default:
        return 'grey.50';
    }
  };

  const getPriorityBorderColor = (priority) => {
    switch (priority) {
      case 'URGENT':
        return 'error.main';
      case 'HIGH':
        return 'warning.main';
      case 'MEDIUM':
        return 'info.main';
      case 'LOW':
        return 'grey.400';
      default:
        return 'grey.300';
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold' }}>
        Approval Pipeline
      </Typography>

      <Grid container spacing={2}>
        {/* 1. Customer Top-ups - Money IN */}
        {summary.customerTopups.count > 0 && (
          <Grid item xs={12} md={6} lg={4}>
            <Card
              sx={{
                p: 2.5,
                height: '100%',
                border: 2,
                borderColor: getPriorityBorderColor(summary.customerTopups.priority),
                bgcolor: getPriorityColor(summary.customerTopups.priority),
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AccountBalanceWalletIcon sx={{ fontSize: 28, color: 'success.main' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Customer Top-ups
                  </Typography>
                </Box>
                <Chip
                  label={`${summary.customerTopups.count} requests`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </Box>

              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main', mb: 1 }}>
                {formatCurrency(summary.customerTopups.total)}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Awaiting Verification
              </Typography>

              <Box sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'background.paper',
                mb: 2
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Money Status:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ✓ In Thaklis bank, awaiting system recording
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                color="success"
                endIcon={<ArrowForwardIcon />}
                onClick={() => handleNavigate(summary.customerTopups.actionUrl)}
              >
                View All & Approve
              </Button>
            </Card>
          </Grid>
        )}

        {/* 2. Partner Payments - Awaiting Approval */}
        {summary.partnerPayments.count > 0 && (
          <Grid item xs={12} md={6} lg={4}>
            <Card
              sx={{
                p: 2.5,
                height: '100%',
                border: 2,
                borderColor: getPriorityBorderColor(summary.partnerPayments.priority),
                bgcolor: getPriorityColor(summary.partnerPayments.priority),
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Partner Payments
                  </Typography>
                </Box>
                <Chip
                  label={`${summary.partnerPayments.count} pending`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>

              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main', mb: 1 }}>
                {formatCurrency(summary.partnerPayments.total)}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Awaiting Approval
              </Typography>

              <Box sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'background.paper',
                mb: 2
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Money Status:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ✓ In Thaklis bank, reserved but not yet paid
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                color="primary"
                endIcon={<ArrowForwardIcon />}
                onClick={() => handleNavigate(summary.partnerPayments.actionUrl)}
              >
                View All & Approve
              </Button>
            </Card>
          </Grid>
        )}

        {/* 3. Approved - Awaiting Bank Transfer */}
        {summary.approvedAwaitingTransfer.count > 0 && (
          <Grid item xs={12} md={6} lg={4}>
            <Card
              sx={{
                p: 2.5,
                height: '100%',
                border: 2,
                borderColor: getPriorityBorderColor(summary.approvedAwaitingTransfer.priority),
                bgcolor: getPriorityColor(summary.approvedAwaitingTransfer.priority),
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ fontSize: 28, color: 'warning.main' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Approved Payments
                  </Typography>
                </Box>
                <Chip
                  label={`${summary.approvedAwaitingTransfer.count} approved`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Box>

              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main', mb: 1 }}>
                {formatCurrency(summary.approvedAwaitingTransfer.total)}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Awaiting Bank Transfer
              </Typography>

              <Box sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'background.paper',
                mb: 2
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Money Status:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ✓ In Thaklis bank, approved - needs transfer
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                color="warning"
                endIcon={<ArrowForwardIcon />}
                onClick={() => handleNavigate(summary.approvedAwaitingTransfer.actionUrl)}
              >
                Record Bank Transfer
              </Button>
            </Card>
          </Grid>
        )}

        {/* 4. In Transit - Awaiting Confirmation ⚠️ NEW */}
        {summary.inTransit.count > 0 && (
          <Grid item xs={12} md={6} lg={4}>
            <Card
              sx={{
                p: 2.5,
                height: '100%',
                border: 2,
                borderColor: getPriorityBorderColor(summary.inTransit.priority),
                bgcolor: getPriorityColor(summary.inTransit.priority),
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HourglassEmptyIcon sx={{ fontSize: 28, color: summary.inTransit.priority === 'URGENT' ? 'error.main' : 'info.main' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    In Transit
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Chip
                    label={`${summary.inTransit.count} unconfirmed`}
                    size="small"
                    color={summary.inTransit.priority === 'URGENT' ? 'error' : 'info'}
                    variant="outlined"
                  />
                  {summary.inTransit.alerts.overdue > 0 && (
                    <Chip
                      icon={<WarningIcon />}
                      label={`${summary.inTransit.alerts.overdue} OVERDUE`}
                      size="small"
                      color="error"
                      sx={{ fontWeight: 'bold' }}
                    />
                  )}
                </Box>
              </Box>

              <Typography variant="h4" sx={{
                fontWeight: 'bold',
                color: summary.inTransit.priority === 'URGENT' ? 'error.main' : 'info.main',
                mb: 1
              }}>
                {formatCurrency(summary.inTransit.total)}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Awaiting Partner Confirmation
              </Typography>

              <Box sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'background.paper',
                mb: 2
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Money Status:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ✗ Left Thaklis bank, awaiting partner confirmation
                </Typography>
              </Box>

              {/* Breakdown by status */}
              <Box sx={{ mb: 2 }}>
                {summary.inTransit.alerts.normal > 0 && (
                  <Typography variant="caption" display="block" sx={{ color: 'success.main', mb: 0.5 }}>
                    • {summary.inTransit.alerts.normal} Normal (0-2 days)
                  </Typography>
                )}
                {summary.inTransit.alerts.followUp > 0 && (
                  <Typography variant="caption" display="block" sx={{ color: 'warning.main', mb: 0.5 }}>
                    • {summary.inTransit.alerts.followUp} Follow-up (3-5 days)
                  </Typography>
                )}
                {summary.inTransit.alerts.overdue > 0 && (
                  <Typography variant="caption" display="block" sx={{ color: 'error.main', fontWeight: 'bold' }}>
                    ⚠️ {summary.inTransit.alerts.overdue} OVERDUE (>5 days)
                  </Typography>
                )}
              </Box>

              <Button
                fullWidth
                variant="contained"
                color={summary.inTransit.priority === 'URGENT' ? 'error' : 'info'}
                endIcon={<ArrowForwardIcon />}
                onClick={() => handleNavigate(summary.inTransit.actionUrl)}
              >
                View Reconciliation Dashboard
              </Button>
            </Card>
          </Grid>
        )}

        {/* 5. Channel Commissions */}
        {summary.channelCommissions.count > 0 && (
          <Grid item xs={12} md={6} lg={4}>
            <Card
              sx={{
                p: 2.5,
                height: '100%',
                border: 2,
                borderColor: getPriorityBorderColor(summary.channelCommissions.priority),
                bgcolor: getPriorityColor(summary.channelCommissions.priority),
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 6
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HandshakeIcon sx={{ fontSize: 28, color: 'secondary.main' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Channel Commissions
                  </Typography>
                </Box>
                <Chip
                  label={`${summary.channelCommissions.count} partners`}
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              </Box>

              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'secondary.main', mb: 1 }}>
                {formatCurrency(summary.channelCommissions.total)}
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Awaiting Approval
              </Typography>

              <Box sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'background.paper',
                mb: 2
              }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Money Status:
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ✓ In Thaklis bank, monthly payout pending
                </Typography>
              </Box>

              <Button
                fullWidth
                variant="contained"
                color="secondary"
                endIcon={<ArrowForwardIcon />}
                onClick={() => handleNavigate(summary.channelCommissions.actionUrl)}
              >
                View All & Approve
              </Button>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Summary Alert */}
      {summary.totals.urgentActions > 0 && (
        <Alert severity="error" sx={{ mt: 3 }} icon={<WarningIcon />}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {summary.totals.urgentActions} URGENT ACTION{summary.totals.urgentActions > 1 ? 'S' : ''} REQUIRED
          </Typography>
          <Typography variant="caption">
            {summary.totals.urgentActions} payment{summary.totals.urgentActions > 1 ? 's' : ''} overdue and requiring immediate investigation.
          </Typography>
        </Alert>
      )}

      {/* Overall Summary */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Total Items Awaiting Action:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {summary.totals.totalAwaitingAction}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Total Amount:
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              {formatCurrency(summary.totals.totalAmount)}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default PendingApprovalsCard;
